import { Fragment, useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ScrollText, ChevronRight, ChevronDown, Loader2, ShieldCheck, AlertTriangle,
  ServerCog, User, RefreshCw,
} from 'lucide-react'
import {
  getMtdLedger, getMtdLedgerPeriods, getMtdLedgerSummary, getMtdLedgerChildren,
  sweepMtdLedger, getAdminClients,
} from '../../lib/api'
import { ToastContainer, useToast } from '../../components/Toast'

// Signed money. Unlike the FedEx pages — where a positive variance means a cost
// went UP and is therefore red — positive here means revenue went UP, so the
// colours are inverted on purpose.
function money(v) {
  const n = Number(v || 0)
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-AU', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`
}
function signedMoney(v) {
  const n = Number(v || 0)
  return `${n > 0 ? '+' : n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-AU', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`
}
function deltaClass(v) {
  const n = Number(v || 0)
  if (n > 0) return 'text-emerald-600'
  if (n < 0) return 'text-red-600'
  return 'text-slate-400'
}
// Stored UTC, displayed in AU time — the period boundary is AU-local, so a UTC
// clock would put early-morning activity on the wrong day.
function when(ts) {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    })
  } catch { return ts }
}
function periodLabel(p) {
  if (!p) return ''
  try {
    return new Date(`${p}T00:00:00`).toLocaleDateString('en-AU', {
      month: 'long', year: 'numeric',
    })
  } catch { return p }
}

// One badge per revenue source, matching services/gl_sync.py::CAUSE.
const CAUSE_STYLES = {
  order:              'bg-blue-100 text-blue-700',
  postage_adjustment: 'bg-sky-100 text-sky-700',
  sku_charge:         'bg-teal-100 text-teal-700',
  warehouse:          'bg-violet-100 text-violet-700',
  storage:            'bg-indigo-100 text-indigo-700',
  return_label:       'bg-amber-100 text-amber-700',
  export_fee:         'bg-fuchsia-100 text-fuchsia-700',
  rounding:           'bg-slate-200 text-slate-600',
  // Legacy causes from the first (snapshot-diff) design.
  unattributed:       'bg-orange-100 text-orange-700',
  month_open:         'bg-slate-200 text-slate-700',
}
const CAUSE_LABEL = {
  order: 'order', postage_adjustment: 'postage', sku_charge: 'SKU',
  warehouse: 'warehouse', storage: 'storage', return_label: 'return',
  export_fee: 'export', rounding: 'rounding',
}
const CAUSE_CHIPS = [
  { key: '',                   label: 'All' },
  { key: 'order',              label: 'Orders' },
  { key: 'sku_charge',         label: 'SKU charges' },
  { key: 'warehouse',          label: 'Warehouse' },
  { key: 'storage',            label: 'Storage' },
  { key: 'return_label',       label: 'Returns' },
  { key: 'export_fee',         label: 'Exports' },
  { key: 'postage_adjustment', label: 'Postage' },
]

function Tile({ label, value, sub, tone = 'default', icon: Icon }) {
  const tones = {
    default: 'text-slate-900',
    good:    'text-emerald-600',
    warn:    'text-amber-600',
  }
  return (
    <div className="card p-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-bold mt-1 flex items-center gap-2 ${tones[tone]}`}>
        {Icon && <Icon size={20} />}
        {value}
      </p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  )
}

function ChildRows({ lineId }) {
  const { data: kids, isLoading, error: kidsError } = useQuery({
    queryKey: ['admin', 'mtd-log', 'children', lineId],
    queryFn: () => getMtdLedgerChildren(lineId),
  })
  if (isLoading) {
    return (
      <tr><td colSpan={8} className="px-4 py-3 bg-slate-50 text-sm text-slate-500">
        <Loader2 size={14} className="inline animate-spin mr-2" />Loading breakdown…
      </td></tr>
    )
  }
  // `kids` is undefined on failure, which the length check below would state as
  // a fact about the ledger ("none was recorded") rather than a failed request.
  if (kidsError) {
    return (
      <tr><td colSpan={8} className="px-4 py-3 bg-red-50 text-sm text-red-700">
        <AlertTriangle size={14} className="inline mr-2 -mt-0.5" />
        Couldn't load the breakdown — this is <b>not</b> a line without one.{' '}
        <span className="font-mono text-xs break-all">
          {kidsError?.response?.data?.detail || kidsError?.message || 'Unknown error'}
        </span>
      </td></tr>
    )
  }
  if (!kids?.length) {
    return (
      <tr><td colSpan={8} className="px-4 py-3 bg-slate-50 text-sm text-slate-400">
        No per-order breakdown was recorded for this line.
      </td></tr>
    )
  }
  return kids.map(k => (
    <tr key={k.id} className="bg-slate-50 border-b border-slate-100">
      <td className="px-4 py-2" />
      <td className="px-4 py-2 text-xs text-slate-400">{when(k.occurred_at)}</td>
      <td className="px-4 py-2 text-xs text-slate-500" colSpan={2}>{k.description}</td>
      <td className="px-4 py-2 text-xs font-mono text-slate-600">{k.order_number || '—'}</td>
      <td className={`px-4 py-2 text-xs text-right font-semibold tabular-nums ${deltaClass(k.delta_amount)}`}>
        {signedMoney(k.delta_amount)}
      </td>
      <td className="px-4 py-2 text-xs text-right text-slate-400 tabular-nums">
        {money(k.mtd_after)}
      </td>
      <td className="px-4 py-2" />
    </tr>
  ))
}

export default function MtdLedger() {
  const { toasts, addToast, removeToast } = useToast()
  const queryClient = useQueryClient()

  const [period, setPeriod]     = useState('')
  const [clientId, setClientId] = useState('')
  const [cause, setCause]       = useState('')
  const [actorType, setActor]   = useState('')
  const [search, setSearch]     = useState('')
  const [minAbs, setMinAbs]     = useState('')
  const [page, setPage]         = useState(1)
  const [expanded, setExpanded] = useState(() => new Set())

  const { data: periods = [] } = useQuery({
    queryKey: ['admin', 'mtd-log', 'periods'],
    queryFn: getMtdLedgerPeriods,
  })
  const { data: clients = [] } = useQuery({
    queryKey: ['admin', 'clients-lite'],
    queryFn: () => getAdminClients(),
    staleTime: 5 * 60 * 1000,
  })

  const activePeriod = period || periods[0] || ''

  const params = useMemo(() => {
    const p = { page, page_size: 50 }
    if (activePeriod) p.period = activePeriod
    if (clientId)  p.client_id = clientId
    if (cause)     p.cause = cause
    if (actorType) p.actor_type = actorType
    if (search)    p.search = search
    if (minAbs !== '' && Number(minAbs) > 0) p.min_abs = Number(minAbs)
    return p
  }, [activePeriod, clientId, cause, actorType, search, minAbs, page])

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'mtd-log', params],
    queryFn: () => getMtdLedger(params),
    keepPreviousData: true,
  })
  const { data: summary, error: summaryError } = useQuery({
    queryKey: ['admin', 'mtd-log', 'summary', activePeriod],
    queryFn: () => getMtdLedgerSummary(activePeriod || undefined),
  })

  const sweep = useMutation({
    mutationFn: () => sweepMtdLedger(activePeriod || undefined),
    onSuccess: (res) => {
      addToast({
        type: 'success',
        message: (res?.new || res?.restated)
          ? `Added ${(res.new || 0).toLocaleString()} new line${res.new === 1 ? '' : 's'}` +
            (res.restated ? ` and ${res.restated.toLocaleString()} restatement${res.restated === 1 ? '' : 's'}.` : '.')
          : 'Nothing to add — every charge is already recorded.',
      })
      queryClient.invalidateQueries({ queryKey: ['admin', 'mtd-log'] })
    },
    onError: (e) => addToast({
      type: 'error',
      message: e?.response?.data?.detail || 'Could not run the reconciliation sweep.',
    }),
  })

  const lines = data?.lines || []
  const total = data?.total || 0
  const pages = Math.max(1, Math.ceil(total / 50))

  function toggle(id) {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function reset(setter) {
    return (v) => { setter(v); setPage(1) }
  }

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ScrollText size={24} /> MTD Change Log
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every dollar movement in month-to-date revenue, with the reason.
            Lines are permanent — they can never be edited or deleted, and the
            log starts fresh each month.
          </p>
        </div>
        <button
          onClick={() => sweep.mutate()}
          disabled={sweep.isPending}
          className="btn-secondary flex items-center gap-2 shrink-0"
          title="Rebuild the ledger: append a line for every charge not yet recorded, and a restatement for any that changed. Only ever adds lines."
        >
          {sweep.isPending
            ? <Loader2 size={16} className="animate-spin" />
            : <RefreshCw size={16} />}
          Reconcile
        </button>
      </div>

      {/* Stat tiles. A failed fetch leaves `summary` undefined and every tile
          falls back to a figure it invents — $0.00 net change, 0 lines, and a
          "Drift / $0.00 unaccounted" reconciliation — so the error replaces
          them rather than sitting beside numbers nobody computed. */}
      {summaryError ? (
        <div className="card p-4 border border-red-200 bg-red-50 flex items-start gap-3">
          <AlertTriangle size={20} className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800">Couldn't load the MTD summary</p>
            <p className="text-xs text-red-700 mt-0.5">
              These totals are <b>unknown</b> — not $0.00, and not a clean reconciliation.
            </p>
            <p className="text-xs text-red-600 font-mono mt-1 break-all">
              {summaryError?.response?.data?.detail || summaryError?.message || 'Unknown error'}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Tile
            label="Net change"
            value={signedMoney(summary?.net_change)}
            sub={periodLabel(activePeriod)}
            tone={Number(summary?.net_change || 0) < 0 ? 'warn' : 'default'}
          />
          <Tile label="Lines recorded" value={(summary?.line_count ?? 0).toLocaleString()} />
          <Tile
            label="Largest single move"
            value={summary?.largest ? signedMoney(summary.largest.delta_amount) : '—'}
            sub={summary?.largest?.client_name || ''}
          />
          <Tile
            label="Reconciliation"
            value={summary?.reconciled ? 'Balanced' : 'Drift'}
            icon={summary?.reconciled ? ShieldCheck : AlertTriangle}
            tone={summary?.reconciled ? 'good' : 'warn'}
            sub={summary?.reconciled
              ? 'Log matches live MTD'
              : `${money(summary?.drift_total)} unaccounted — press Reconcile`}
          />
        </div>
      )}

      {/* Filters */}
      <div className="card px-4 py-3 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={activePeriod}
            onChange={e => reset(setPeriod)(e.target.value)}
            className="input text-sm w-auto"
          >
            {periods.map(p => (
              <option key={p} value={p}>{periodLabel(p)}</option>
            ))}
          </select>
          <select
            value={clientId}
            onChange={e => reset(setClientId)(e.target.value)}
            className="input text-sm w-auto"
          >
            <option value="">All clients</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={actorType}
            onChange={e => reset(setActor)(e.target.value)}
            className="input text-sm w-auto"
          >
            <option value="">Anyone</option>
            <option value="admin">Admin</option>
            <option value="worker">Automated</option>
          </select>
          <input
            type="search"
            value={search}
            onChange={e => reset(setSearch)(e.target.value)}
            placeholder="Search reason…"
            className="input text-sm flex-1 min-w-[12rem]"
          />
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Min $</span>
            <input
              type="number" min="0" step="0.01"
              value={minAbs}
              onChange={e => reset(setMinAbs)(e.target.value)}
              className="input text-sm w-20"
              placeholder="0.00"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {CAUSE_CHIPS.map(c => (
            <button
              key={c.key || 'all'}
              onClick={() => reset(setCause)(c.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                cause === c.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="w-8" />
                <th className="px-4 py-3 text-left">When</th>
                <th className="px-4 py-3 text-left">Client</th>
                <th className="px-4 py-3 text-left">What changed</th>
                <th className="px-4 py-3 text-left">Order</th>
                <th className="px-4 py-3 text-right">Change</th>
                <th className="px-4 py-3 text-right">Balance</th>
                <th className="px-4 py-3 text-left">Who</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                  <Loader2 size={20} className="inline animate-spin mr-2" />Loading…
                </td></tr>
              )}
              {isError && !isLoading && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-red-500 text-sm">
                  {error?.response?.data?.detail || 'Could not load the change log.'}
                </td></tr>
              )}
              {!isLoading && !isError && lines.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400 text-sm">
                  No changes recorded for {periodLabel(activePeriod)} yet. Lines
                  appear as soon as anything moves MTD — and once written they can
                  never be edited or deleted.
                </td></tr>
              )}
              {lines.map(l => {
                const isOpen = expanded.has(l.id)
                const many = Number(l.rows_affected || 1) > 1
                return (
                  <Fragment key={l.id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="pl-2">
                        {many && (
                          <button
                            onClick={() => toggle(l.id)}
                            className="text-slate-400 hover:text-slate-600"
                            aria-label={isOpen ? 'Collapse breakdown' : 'Expand breakdown'}
                          >
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">
                        {when(l.occurred_at)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-700">
                        {l.client_name || <span className="text-slate-400 italic">(unlinked)</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold mr-2 align-middle ${
                          CAUSE_STYLES[l.cause] || 'bg-slate-100 text-slate-600'
                        }`}>
                          {CAUSE_LABEL[l.cause] || String(l.cause || '').replace(/_/g, ' ')}
                        </span>
                        {l.description}
                        {many && (
                          <span className="ml-2 text-[10px] text-slate-400 whitespace-nowrap">
                            × {Number(l.rows_affected).toLocaleString()} orders
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-slate-600">
                        {l.order_number || '—'}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-bold tabular-nums ${deltaClass(l.delta_amount)}`}>
                        {signedMoney(l.delta_amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-slate-700 tabular-nums">
                        {money(l.mtd_after)}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1.5">
                          {l.actor_type === 'admin'
                            ? <User size={12} className="text-slate-400" />
                            : <ServerCog size={12} className="text-slate-400" />}
                          <span className="truncate max-w-[10rem]" title={l.actor_email || l.actor_label}>
                            {l.actor_email || l.actor_label || 'system'}
                          </span>
                        </span>
                      </td>
                    </tr>
                    {isOpen && <ChildRows lineId={l.id} />}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              {total.toLocaleString()} line{total === 1 ? '' : 's'} · page {page} of {pages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-secondary text-xs disabled:opacity-40"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(pages, p + 1))}
                disabled={page >= pages}
                className="btn-secondary text-xs disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
